import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { formatDate } from '@/components/documents/format'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { type FileKind, filePath, type TimelineItem, useExpiryTimeline } from '@/models/vault'
import { BackLink } from './BackLink'
import { SlotStatusPill } from './SlotStatusPill'
import { useVault } from './VaultContext'

const ALL = 'all'

const KINDS: { value: FileKind | typeof ALL; label: string }[] = [
  { value: ALL, label: 'Everyone' },
  { value: 'carrier', label: 'Carrier' },
  { value: 'drivers', label: 'Drivers' },
  { value: 'trucks', label: 'Trucks' },
  { value: 'trailers', label: 'Trailers' },
]

interface Group {
  key: string
  label: string
  items: TimelineItem[]
}

/** Expired first, then month by month, then what never lapses. */
export function groupByMonth(items: TimelineItem[], today = new Date()): Group[] {
  const todayKey = today.toISOString().slice(0, 10)
  const groups = new Map<string, Group>()
  for (const item of items) {
    let key: string
    let label: string
    if (!item.dateExpiration) {
      key = 'never'
      label = 'No expiry'
    } else if (item.dateExpiration < todayKey) {
      key = 'expired'
      label = 'Expired'
    } else {
      key = item.dateExpiration.slice(0, 7)
      const [year, month] = key.split('-').map(Number)
      label = new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    }
    const group = groups.get(key) ?? { key, label, items: [] }
    group.items.push(item)
    groups.set(key, group)
  }
  // Items arrive soonest first, so insertion order is the timeline's order.
  return [...groups.values()]
}

function TimelineRow({ item }: { item: TimelineItem }) {
  const { href } = useVault()
  return (
    <Link
      to={href(filePath(item.fileKind, item.subjectId ?? undefined))}
      // So the file's back link leads here, not to the folder it would otherwise assume.
      state={{ from: 'timeline' }}
      className="hover:bg-muted/50 flex items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-colors"
    >
      <span className="text-muted-foreground w-24 shrink-0 text-sm">
        {item.dateExpiration ? formatDate(item.dateExpiration) : 'No expiry'}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{item.subjectName}</span>
        <span className="text-muted-foreground truncate text-sm">{item.slotLabel}</span>
      </span>
      <SlotStatusPill status={item.status} />
      <ChevronRight className="text-muted-foreground size-4 shrink-0" />
    </Link>
  )
}

export function ExpiryTimeline() {
  const { orgId, href } = useVault()
  const [slotKey, setSlotKey] = useState<string>(ALL)
  const [kind, setKind] = useState<string>(ALL)
  const { items, total, types, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useExpiryTimeline(orgId, {
      fileKind: kind === ALL ? undefined : (kind as FileKind),
      slotKey: slotKey === ALL ? undefined : slotKey,
    })
  const groups = groupByMonth(items)
  const dated = groups.filter((group) => group.key !== 'never')
  const never = groups.find((group) => group.key === 'never')

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackLink to={href('')} label="Vault" />
        <div className="flex flex-wrap gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-40" aria-label="Whose documents">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={slotKey} onValueChange={setSlotKey}>
            <SelectTrigger className="w-64" aria-label="Document type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All document types</SelectItem>
              {types.map((type) => (
                <SelectItem key={type.key} value={type.key}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">Expiry timeline</h2>
        <span className="text-muted-foreground text-sm">
          {items.length} of {total} dated documents
        </span>
      </div>

      {isLoading ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing on file matches.</p>
      ) : (
        <>
          {/* One rail down the left; each month hangs a dot on it, the rows sit beside it. */}
          {dated.length > 0 && (
            <div className="relative pl-9">
              <span aria-hidden="true" className="bg-ink-200 absolute top-3 bottom-3 left-[7px] w-px" />
              {dated.map((group) => (
                <section key={group.key} className="flex flex-col gap-2 pb-6 last:pb-0">
                  <h3 className="text-muted-foreground relative text-xs font-semibold uppercase">
                    <span
                      aria-hidden="true"
                      data-slot="timeline-dot"
                      className="bg-ink-400 absolute top-1/2 -left-9 size-[15px] -translate-y-1/2 rounded-full border-4 border-white"
                    />
                    {group.label}
                  </h3>
                  {group.items.map((item) => (
                    <TimelineRow key={`${item.slotKey}-${item.documentId}`} item={item} />
                  ))}
                </section>
              ))}
            </div>
          )}
          {/* What never lapses has no place on a timeline; it sits below where the rail ends. */}
          {never && (
            <section className="flex flex-col gap-2 pl-9">
              <h3 className="text-muted-foreground text-xs font-semibold uppercase">{never.label}</h3>
              {never.items.map((item) => (
                <TimelineRow key={`${item.slotKey}-${item.documentId}`} item={item} />
              ))}
            </section>
          )}
          {hasNextPage && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                loading={isFetchingNextPage ? 'Loading…' : undefined}
                onClick={() => void fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
